import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import httpStatus from 'http-status';
import config from '../config/config';
import { toast } from 'react-toastify';
import useTokenManagement from '../hooks/user/auth0/useTokenManagement';

interface ICredential {
  email: string;
  password: string;
}

interface IAuthContext {
  email?: string;
  isLoading?: boolean;
  name?: string;
  login?: (credential: ICredential) => Promise<void>;
  logout?: () => void;
  getRole?: () => void;
  isAccessToken?: boolean;
}

interface IAuthProvider {
  children: React.ReactNode;
}

export const AuthContext = createContext<IAuthContext>({
  email: '',
  isLoading: false,
  login: async (credential) => {},
  logout: () => {},
  isAccessToken: false,
});

const AuthProvider = ({ children }: IAuthProvider): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const { setToken, removeToken, getToken } = useTokenManagement();

  const accessToken = getToken('accessToken');
  const [isAccessToken, setIsAccessToken] = useState(!!accessToken);

  const email = localStorage.getItem('email');
  const name = getToken('name');

  const login = async (credential: ICredential): Promise<any> => {
    const { email, password } = credential;
    if (!email.trim() && !password.trim()) return;
    try {
      setIsLoading(true);
      const API = `${config.AUTH_API}/login`;
      const { status, data } = await axios.post(API, {
        email,
        password,
      });
      if (status === httpStatus.OK && data.accessToken) {
        setIsAccessToken(true);
        const { accessToken, email, adminTag, refreshToken, name } = data;
        setToken('accessToken', accessToken);
        setToken('refreshToken', refreshToken);
        setToken('name', name);
        localStorage.setItem('adminTag', JSON.stringify(adminTag));
        localStorage.setItem('email', email);
        toast.success('Login success!');
        return true;
      }
    } catch (error) {
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    localStorage.clear();
    removeToken('accessToken');
    removeToken('refreshToken');
    setIsAccessToken(false);
  };

  return (
    <AuthContext.Provider
      value={{
        email,
        isLoading,
        name,
        login,
        logout,
        isAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContext => useContext(AuthContext);

export { AuthProvider, useAuth };
